import { inject, computed } from 'vue'
import { resolveFlavor } from '@/config/flavors'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable for navigating to the management app with authentication token
 * 
 * Usage:
 *   const { openManagementApp, hasManagementApp, managementAppUrl } = useManagementApp()
 *   
 *   // Check if management app is configured for current flavor
 *   if (hasManagementApp.value) {
 *     openManagementApp() // Opens management app with token
 *   }
 */
export const useManagementApp = () => {
  const flavor = inject('flavor', resolveFlavor())
  const authStore = useAuthStore()

  // Environment variable override for local development
  const envOverrideUrl = import.meta.env.VITE_MANAGEMENT_APP_URL || null

  /**
   * Management app URL - env variable overrides flavor config for local dev
   */
  const managementAppUrl = computed(() => {
    // Env variable takes precedence (for local development)
    if (envOverrideUrl) return envOverrideUrl
    // Otherwise use flavor-specific URL
    return flavor?.managementAppUrl || null
  })

  /**
   * Whether management app is configured for current flavor
   */
  const hasManagementApp = computed(() => {
    return Boolean(managementAppUrl.value)
  })

  /**
   * Whether user is authenticated and can open management app
   */
  const canOpenManagementApp = computed(() => {
    return hasManagementApp.value && authStore.isAuthenticated
  })

  /**
   * Get the current auth token
   */
  const getToken = () => {
    return authStore.session?.accessToken || authStore.session?.token || null
  }

  /**
   * Build the management app URL with token parameter
   * @param {string} [path] - Optional path to navigate to (e.g., '/bookings')
   * @returns {string|null}
   */
  const buildManagementAppUrl = (path = '') => {
    if (!managementAppUrl.value) return null

    const token = getToken()
    const baseUrl = managementAppUrl.value.replace(/\/$/, '')
    const targetPath = path ? `/${path.replace(/^\//, '')}` : ''
    
    const url = new URL(`${baseUrl}${targetPath}`)
    
    if (token) {
      url.searchParams.set('sso_token', token)
    }
    
    return url.toString()
  }

  /**
   * Open management app in a new tab with the auth token
   * @param {string} [path] - Optional path to navigate to
   * @returns {boolean} - Whether the window was opened
   */
  const openManagementApp = (path = '') => {
    const url = buildManagementAppUrl(path)
    if (!url) {
      console.warn('Management app URL not configured for this flavor')
      return false
    }

    window.open(url, '_blank', 'noopener,noreferrer')
    return true
  }

  /**
   * Navigate to management app in same tab with the auth token
   * @param {string} [path] - Optional path to navigate to
   * @returns {boolean} - Whether navigation occurred
   */
  const goToManagementApp = (path = '') => {
    const url = buildManagementAppUrl(path)
    if (!url) {
      console.warn('Management app URL not configured for this flavor')
      return false
    }

    window.location.href = url
    return true
  }

  return {
    managementAppUrl,
    hasManagementApp,
    canOpenManagementApp,
    buildManagementAppUrl,
    openManagementApp,
    goToManagementApp,
    getToken
  }
}
